import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet'; // Added Helmet for SEO
import { getAllAppointments } from '../../actions/appointmentActions';
import { getAllPatientProfiles } from '../../actions/patientProfileActions';
import { imgUrl } from '../../actions/types';
import { Link } from 'react-router-dom'; 
// Helper function to calculate age from a date of birth string
const calculateAge = (dobString) => {
  if (!dobString) return 'N/A';
  const birthDate = new Date(dobString);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return isNaN(age) ? 'N/A' : age;
};

const PatientList = ({ appointment, profiles, getAllAppointments, getAllPatientProfiles }) => {
  const hospitalCode = parseInt(localStorage.getItem('hospitalCode'), 10);
  const history = useHistory();

  // --- STATE MANAGEMENT FOR FILTERS AND SORTING ---
  const [searchTerm, setSearchTerm] = useState('');
  const [bloodGroupFilter, setBloodGroupFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState(''); // State for the new Gender filter
  const [sortBy, setSortBy] = useState('name_asc');
  useEffect(() => {
    getAllAppointments(hospitalCode);
    getAllPatientProfiles();
  }, [getAllAppointments, getAllPatientProfiles]);

  // --- DATA PROCESSING & OPTIMIZATION ---

  const basePatientsForHospital = useMemo(() => {
    const relevantAppointments = appointment.filter(
      (apt) => Number(apt.hospitalID) === hospitalCode
    );
    const uniquePatientIDs = [...new Set(relevantAppointments.map((apt) => apt.PatientID))];
    return profiles
      .filter((p) => uniquePatientIDs.includes(p.PatientID))
      .map(p => ({ ...p, calculatedAge: calculateAge(p.dateOfBirth) }));
  }, [appointment, profiles, hospitalCode]);

  const uniqueBloodGroups = useMemo(() => {
    return [...new Set(basePatientsForHospital.map(p => p.bloodGrp).filter(Boolean))].sort();
  }, [basePatientsForHospital]);

  const filteredAndSortedPatients = useMemo(() => {
    let processedPatients = [...basePatientsForHospital];

    // Apply text, blood group, and gender filters
    if (searchTerm || bloodGroupFilter || genderFilter) {
      processedPatients = processedPatients.filter(patient => {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const searchableContent = `
          ${patient.firstName} ${patient.lastName} ${patient.PatientID} ${patient.phoneNumber || ''}
        `.toLowerCase();

        const matchesSearchTerm = searchTerm ? searchableContent.includes(lowerCaseSearchTerm) : true;
        const matchesBloodGroup = bloodGroupFilter ? patient.bloodGrp === bloodGroupFilter : true;
        const matchesGender = genderFilter ? patient.gender === genderFilter : true; // Logic for gender filter

        return matchesSearchTerm && matchesBloodGroup && matchesGender;
      });
    }

    // Apply sorting
    processedPatients.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc': return a.firstName.localeCompare(b.firstName);
        case 'id_asc': return a.PatientID - b.PatientID;
        case 'id_desc': return b.PatientID - a.PatientID;
        case 'age_asc': return a.calculatedAge - b.calculatedAge;
        case 'age_desc': return b.calculatedAge - a.calculatedAge;
        default: return 0;
      }
    });

    return processedPatients;
  }, [basePatientsForHospital, searchTerm, bloodGroupFilter, genderFilter, sortBy]);


  const viewPatientProfile = (patientId) => {
    history.push(`/doctor/PatientProfilePage/${patientId}`);
  };

  return (
    <>
    <div>
       <div className="page-header filter-bar rounded ">
                <div className="row align-items-center">
                    <div className="col">
                        <h4 className="page-title">My patients</h4>
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item"><Link to="/doctor/dashboard">Doctor Dashboard</Link></li>
                            <li className="breadcrumb-item active">My patients</li>
                        </ul>
                    </div>
                </div>
            </div>
      <Helmet>
        <title>Patient List - Doctor Dashboard | Medisevas</title>
        <meta name="description" content="View, search, and manage your patient list. Filter by name, blood group, gender, and sort according to your preference." />
      </Helmet>

      {/* --- FILTER & SORT CONTROLS UI --- */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-light border-bottom">
          <h4 className="card-title mb-0 d-flex align-items-center">
            <i className="fas fa-filter mr-2 text-dark"></i> Find & Organize Patients
          </h4>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <label>Search by Name, ID, Phone</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., Jane Doe..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <label>Blood Group</label>
                <select
                  className="form-control"
                  value={bloodGroupFilter}
                  onChange={(e) => setBloodGroupFilter(e.target.value)}
                >
                  <option value="">All Groups</option>
                  {uniqueBloodGroups.map(group => (
                    <option key={group} value={group}>{group}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* New Gender Filter */}
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <label>Gender</label>
                <select
                  className="form-control"
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                >
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <label>Sort By</label>
                <select
                  className="form-control"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="name_asc">Name (A-Z)</option>
                  <option value="id_asc">Patient ID (Asc)</option>
                  <option value="id_desc">Patient ID (Desc)</option>
                  <option value="age_asc">Age (Youngest First)</option>
                  <option value="age_desc">Age (Oldest First)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- PATIENT LIST DISPLAY --- */}
      <div className='card'>
<div className='card-body'>
      <div className="row row-grid">
        {basePatientsForHospital.length === 0 ? (
          <div className="col-12 text-center mt-5">
            <div className="alert alert-info">
              <h5>No Patients Found</h5>
              <p className="mb-0">There are no patients with scheduled appointments at your hospital yet.</p>
            </div>
          </div>
        ) : filteredAndSortedPatients.length === 0 ? (
          <div className="col-12 text-center mt-5">
            <div className="alert alert-warning">
              <h5>No Matching Patients</h5>
              <p className="mb-0">No patients match your current filter criteria. Try adjusting the filters.</p>
            </div>
          </div>
        ) : (
          filteredAndSortedPatients.map((patient) => (
            <div
              className="col-md-6 col-lg-4 col-xl-3 mb-2"
              key={patient.PatientID}
              onClick={() => window.location.href = `#/patient-profile/${patient.PatientID}`}
              style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
              onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)'; }}
              onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div className="card widget-profile pat-widget-profile h-100">
                <div className="card-body">
                  <div className="pro-widget-content">
                    <div className="profile-info-widget">
                      <div className="booking-doc-img">
                        <img
                          src={patient.uploadPhoto ? `${imgUrl}${patient.uploadPhoto}` : 'assets/img/patients/patient.jpg'}
                          alt="Patient"
                        />
                      </div>
                      <div className="profile-det-info">
                        <h3>{patient.firstName} {patient.lastName}</h3>
                        <div className="patient-details">
                          <h5><b>Patient ID:</b> {patient.PatientID}</h5>
                          <h5 className="mb-0">
                            <i className="fas fa-map-marker-alt" /> {patient.city || 'N/A'}
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="patient-info">
                    <ul>
                      <li>Gender <span>{patient.gender}</span></li>

                      <li>Age <span>{patient.calculatedAge} Years</span></li>
                      <li>Blood Group <span className="text-danger font-weight-bold">{patient.bloodGrp || 'N/A'}</span></li>

                      <li>Email <span >{patient.email || 'N/A'}</span></li>
                      <li>Phone <span>{patient.phoneNumber || 'N/A'}</span></li>

                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
</div>
</div>
    </>
  );
};

const mapStateToProps = (state) => ({
  appointment: state.appointment?.all || [],
  profiles: state.patientProfile?.profiles || [],
});

export default connect(mapStateToProps, {
  getAllAppointments,
  getAllPatientProfiles,
})(PatientList);